import { z } from 'zod';
import { createBidirectionalEnum } from '@/features/devices/schemas/devices.utils.ts';

export const DEVICE_TYPES = {
  Inverter: 10,
  BMS: 20,
  SmartMeter: 30,
  SmartLogger: 40,
  Dongle: 50,
  Logger: 60,
  PQConnect: 70,
  Battery: 80,
  Sensor: 90,
  Unknown: 1000,
} as const;

export const PLANT_MEASUREMENT_METADATA = {
  'MQTT Connection Status': 4001,
} as const;

export const COMMON_DEVICE_FIELDS = {
  'ModBus Status': 1,
  'Event Status': 5001,
} as const;

export const DEVICE_TYPES_NAMES_ARRAY = Object.keys(DEVICE_TYPES) as [
  keyof typeof DEVICE_TYPES,
];

export const DEVICE_TYPES_CODES_ARRAY = Object.values(DEVICE_TYPES) as [
  (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES],
];

export const DEVICE_TYPE_OPTIONS: {
  value: DeviceTypeCodes;
  label: DeviceTypeName;
}[] = (Object.entries(DEVICE_TYPES) as [DeviceTypeName, DeviceTypeCodes][]).map(
  ([label, value]) => ({ value, label })
);

const DEVICE_TYPES_MAP = createBidirectionalEnum(DEVICE_TYPES);
export const COMMON_DEVICE_FIELDS_MAP = createBidirectionalEnum(COMMON_DEVICE_FIELDS);
export const PLANT_MEASUREMENT_MAP = createBidirectionalEnum(PLANT_MEASUREMENT_METADATA);

export const DEVICE_TYPE_NAME_BY_CODE = DEVICE_TYPES_MAP.byValue;
export const DEVICE_TYPE_CODE_BY_NAME = DEVICE_TYPES_MAP.byKey;
export const DEVICE_FIELD_NAME_BY_CODE = COMMON_DEVICE_FIELDS_MAP.byValue;
export const DEVICE_FIELD_CODE_BY_NAME = COMMON_DEVICE_FIELDS_MAP.byKey;
export const PLANT_MEASUREMENT_NAME_BY_CODE = PLANT_MEASUREMENT_MAP.byValue;
export const PLANT_MEASUREMENT_CODE_BY_NAME = PLANT_MEASUREMENT_MAP.byKey;

export type DeviceTypeName = keyof typeof DEVICE_TYPE_CODE_BY_NAME;
export type DeviceTypeCodes = (typeof DEVICE_TYPE_CODE_BY_NAME)[DeviceTypeName];
export type CommonDeviceFieldName = keyof typeof DEVICE_FIELD_CODE_BY_NAME;
export type CommonDeviceFieldCodes = (typeof DEVICE_FIELD_CODE_BY_NAME)[CommonDeviceFieldName];
export type PlantMeasurementName = keyof typeof PLANT_MEASUREMENT_CODE_BY_NAME;
export type PlantMeasurementCodes =
  (typeof PLANT_MEASUREMENT_CODE_BY_NAME)[PlantMeasurementName];

export const deviceTypeNameEnum = z.enum(DEVICE_TYPES_NAMES_ARRAY, {
  error: 'Device type is required',
});
export type DeviceTypeNames = z.infer<typeof deviceTypeNameEnum>;

export const deviceTypeIdEnum = z.enum(DEVICE_TYPES, {
  error: 'Device type is required',
});
export type DeviceTypeIds = z.infer<typeof deviceTypeIdEnum>;

export const DeviceSchema = z.object({
  plantId: z.string({
    error: 'Plant ID is required',
  }),
  deviceId: z.string({
    error: 'Device ID is required',
  }),

  //
  name: z
    .string({
      error: 'Device name is required',
    })
    .min(3, {
      message: 'Device name must be at least 3 characters long',
    }),
  sn: z
    .string({
      error: 'Serial number is required',
    })
    .min(3, {
      message: 'Serial number must be at least 3 characters long',
    })
    .regex(/^[A-Za-z0-9._:/-]+$/, {
      message:
        'Serial number can only contain letters, digits, and the characters . : / _ - (e.g. INV-2024.001)',
    }),

  typeId: deviceTypeIdEnum.describe('Device type id'),
  type: deviceTypeNameEnum.describe('Device type'),

  model: z
    .string({
      error: 'Device model is required',
    })
    .nullable()
    .transform((val) => val ?? 'Unknown')
    .default('Unknown'),
  supplier: z
    .string({
      error: 'Supplier name is required',
    })
    .transform((val) => val ?? 'Unknown')
    .default('Unknown'),

  //
  showInDashboard: z
    .boolean({
      error: 'Show in dashboard is required',
    })
    .transform((val) => val ?? false)
    .default(false),
  timezone: z.string({
    error: 'Timezone is required',
  }),

  //
  connectedAt: z
    .string({
      error: 'Connected at is required',
    })
    .nullable()
    .transform((val) => val ?? ''),
  updatedAt: z
    .string({
      error: 'Updated at is required',
    })
    .transform((val) => val ?? new Date().toISOString())
    .default(new Date().toISOString()),
  createdAt: z
    .string({
      error: 'Created at is required',
    })
    .transform((val) => val ?? new Date().toISOString())
    .default(new Date().toISOString()),
});

export type TDevice = z.infer<typeof DeviceSchema>;
export type TDevices = z.infer<typeof DeviceSchema>[];

export type TTreeDevice = z.input<typeof DeviceSchema> & {
  subDevices?: TTreeDevice[];
};

export const TreeDeviceSchema: z.ZodType<TTreeDevice> = DeviceSchema.extend({
  subDevices: z.lazy(() => TreeDeviceSchema.array()).optional(),
});

export const collectDeviceSns = (devices: TTreeDevice[]): Set<string> => {
  const sns = new Set<string>();
  const walk = (list: TTreeDevice[]) => {
    for (const device of list) {
      if (device.sn) sns.add(device.sn);
      if (device.subDevices?.length) walk(device.subDevices);
    }
  };
  walk(devices);
  return sns;
};

export const createDeviceSchemaWithSnCheck = (
  existingSns: ReadonlySet<string>,
  excludeSn?: string
) =>
  DeviceSchema.superRefine((data, ctx) => {
    if (excludeSn && data.sn === excludeSn) return;
    if (existingSns.has(data.sn)) {
      ctx.addIssue({
        code: 'custom',
        path: ['sn'],
        message: 'Serial number already exists',
      });
    }
  });

export const createDefaultDevice = ({
  plantId,
  timezone,
}: {
  plantId: string;
  timezone: string;
}): TDevice => {
  return {
    plantId,
    deviceId: '',

    name: '',
    sn: '',

    typeId: 1000,
    type: 'Unknown',

    model: 'Unknown',
    supplier: 'Unknown',

    showInDashboard: false,
    timezone,

    connectedAt: '',
    updatedAt: '',
    createdAt: '',
  };
};
